import React from 'react';
import PropTypes from 'prop-types';
import { get, noop } from 'lodash';
import { BaseCard, BaseCardBody } from '@unite-us/ui';
import {
  useFindRecord,
  usePopulate,
} from 'src/api/APIHooks';
import { UPDATE_FEE_SCHEDULE_PROVIDED_SERVICE } from 'src/components/Cases/constants';
import sortProvidedServicesByStartsAt from 'src/components/Cases/utils/sortProvidedServicesByStartsAt';
import useInvoiceSpend from 'src/components/ServiceAuthorization/useInvoiceSpend';
import CardData from './CardData';
import CardHeader from './CardHeader';
import ProvidedServiceFormWrapper from '../form/ProvidedServiceFormWrapper';

const ProvidedServiceCards = ({
  contactId,
  editMode,
  editOnFirstRender,
  providedServices,
  isEditing,
  feeScheduleProgram,
  selectedProvidedServiceId,
  serviceCase,
  showServicesProvidedInteractions,
}) => {
  usePopulate(
    'fee_schedule',
    'fee_schedule',
    feeScheduleProgram,
    { queryConfig: { placeholderData: undefined, enabled: !!feeScheduleProgram } },
  );

  const feeSchedule = feeScheduleProgram?.fee_schedule;
  const ignoreSocialCareExpiredDates = get(feeSchedule, 'ignore_social_care_expired_dates', false);

  const isBillable = feeScheduleProgram?.billable;
  const paymentType = feeScheduleProgram?.payment_type;
  const metafields = feeScheduleProgram?.metafields ?? [];
  const canInvoiceAboveRemainingAuthorizedAmount = feeScheduleProgram?.can_invoice_above_remaining_authorized_amount;
  const unit = feeScheduleProgram?.unit || '';
  const capInformation = feeScheduleProgram?.cap_information || '';

  const edit = (providedServiceId) => () => {
    editMode(providedServiceId);
  };

  const { data: serviceAuthorizationData } = useFindRecord(
    'service_authorization',
    serviceCase?.service_authorization?.id,
    { queryConfig: { placeholderData: undefined, enabled: !!serviceCase?.service_authorization?.id } },
  );
  const serviceAuthorization = serviceAuthorizationData?.data?.data;
  usePopulate('insurance', 'insurance', serviceAuthorization, { queryConfig: { placeholderData: undefined } });
  usePopulate('insurance.plan', 'plan', serviceAuthorization, { queryConfig: { placeholderData: undefined } });

  const serviceAuthorizationInvoiceInformation = useInvoiceSpend(
    get(serviceCase, 'service_authorization.id'),
  );

  return (
    <div className="space-y-5">
      {
        providedServices.length !== 0 &&
        sortProvidedServicesByStartsAt(providedServices).map((providedService) => (
          isEditing && (selectedProvidedServiceId === providedService.id) ? (
            <ProvidedServiceFormWrapper
              key={providedService.id}
              contactId={contactId}
              serviceCase={serviceCase}
              providedService={providedService}
              isBillable={isBillable}
              cancelFunction={() => editMode('')}
              formName={`${UPDATE_FEE_SCHEDULE_PROVIDED_SERVICE}-${providedService.id}`}
              isEditing={isEditing}
              paymentType={paymentType}
              editOnFirstRender={editOnFirstRender}
            />
          ) : (
            <BaseCard key={providedService.id} className="fee-schedule-provided-service-card">
              <BaseCardBody>
                <div data-testid={`provided-service-card-${providedService.id}`}>
                  <CardHeader
                    className="py-4 px-8"
                    isBillable={isBillable}
                    metafields={metafields}
                    onEdit={edit(providedService.id)}
                    programName={get(serviceCase, 'program.name')}
                    providedService={providedService}
                    showInteractions={showServicesProvidedInteractions}
                    ignoreSocialCareExpiredDates={ignoreSocialCareExpiredDates}
                    canInvoiceAboveRemainingAuthorizedAmount={canInvoiceAboveRemainingAuthorizedAmount}
                    feeScheduleProgram={feeScheduleProgram}
                    serviceAuthorization={serviceAuthorization}
                    serviceAuthorizationInvoiceInformation={serviceAuthorizationInvoiceInformation}
                    personId={get(serviceCase, 'person.id')}
                  />
                  <hr className="border-dark-fill-blue" />
                  <CardData
                    capInformation={capInformation}
                    className="py-4 px-8 box-border"
                    paymentType={paymentType}
                    programName={get(serviceCase, 'program.name')}
                    providedService={providedService}
                    unit={unit}
                    metafields={metafields}
                  />
                </div>
              </BaseCardBody>
            </BaseCard>
          )
        ))
      }
    </div>
  );
};

ProvidedServiceCards.propTypes = {
  contactId: PropTypes.string,
  editMode: PropTypes.func,
  providedServices: PropTypes.array.isRequired,
  isEditing: PropTypes.bool,
  editOnFirstRender: PropTypes.bool,
  serviceCase: PropTypes.shape({
    id: PropTypes.string,
    contact: PropTypes.object,
    service_authorization: PropTypes.object,
  }).isRequired,
  selectedProvidedServiceId: PropTypes.string,
  showServicesProvidedInteractions: PropTypes.bool,
  feeScheduleProgram: PropTypes.object,
};

ProvidedServiceCards.defaultProps = {
  contactId: '',
  editMode: noop,
  isEditing: false,
  editOnFirstRender: false,
  feeScheduleProgram: undefined,
  selectedProvidedServiceId: '',
  showServicesProvidedInteractions: true,
};

export default ProvidedServiceCards;
